import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useMatches,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import {
  createServerClient,
  parseCookieHeader,
  serializeCookieHeader,
} from '@supabase/ssr';
import { Session } from '@supabase/supabase-js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangeLanguage } from 'remix-i18next/react';
import { PublicEnvironmentProvider } from './components/common/public-environment';
import { Toaster } from './components/ui/sonner';
import { env } from './env/env';
import { ClientEnvironment } from './env/types';
import i18server, { localeCookie } from './i18n/server';
import { ApiProvider } from './lib/providers/api-provider';
import { EnvironmentProvider } from './lib/providers/environment-provider';
import { SupabaseProvider } from './lib/providers/supabase-provider';
import { ThemeProviderServerHydrated } from './lib/providers/theme-provider';
import { UserProvider } from './lib/providers/user-provider';
import { cn } from './lib/utils';
import { themeCookie } from './lib/utils/cookies.server';
import { isDarkUrl, themeSchema } from './lib/utils/theme-utils';
import './tailwind.css';

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  // Roboto - Light (300) variant
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;1,300&display=swap',
  },
  // Roboto - SemiBold (600) variant
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,600;1,600&display=swap',
  },
  // Roboto - Bold (700) variant
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,700;1,700&display=swap',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap',
  },
];

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Loader for env
export async function loader({ request }: LoaderFunctionArgs) {
  const locale = await i18server.getLocale(request);

  // Get theme data
  const cookieHeader = request.headers.get('Cookie');
  const userTheme = await themeCookie.parse(cookieHeader);

  // If the user is on a dark URL, use the dark theme.
  const theme = isDarkUrl(request.url) ? 'dark' : userTheme;

  // Get initial session from server
  const headers = new Headers();
  const supabase = createServerClient(
    env.SUPABASE_URL!,
    env.SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll() {
          return parseCookieHeader(request.headers.get('Cookie') ?? '');
        },
        setAll(cookiesToSet) {
          cookiesToSet.forEach(({ name, value, options }) =>
            headers.append(
              'Set-Cookie',
              serializeCookieHeader(name, value, options),
            ),
          );
        },
      },
    },
  );

  const {
    data: { user },
  } = await supabase.auth.getUser();

  // This is a workaround to get the session data without the user
  // Getting the session WITH the user will result in supabase warnings
  // Issue: https://github.com/supabase/auth-js/issues/873#issuecomment-2071790000
  const { data } = await supabase.auth.getSession();
  const session: Session | null =
    data.session !== null && user !== null
      ? {
          user,
          provider_token: data.session.provider_token,
          provider_refresh_token: data.session.provider_refresh_token,
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
          expires_in: data.session.expires_in,
          expires_at: data.session.expires_at,
          token_type: data.session.token_type,
        }
      : null;

  return {
    locale,
    theme,
    // Add the user and session to the loader data so it can be used to seed the
    // user provider. Both are nullable since some routes may not require a user
    // or session.
    user,
    session,
    headers: {
      'Set-Cookie': await localeCookie.serialize(locale),
      ...Object.fromEntries(headers.entries()),
    },
    ENV: {
      NODE_ENV: env.NODE_ENV,
      API_URL: env.API_URL,
      SUPABASE_URL: env.SUPABASE_URL,
      SUPABASE_ANON_KEY: env.SUPABASE_ANON_KEY,

      SENTRY_DSN: env.SENTRY_DSN,
      SENTRY_ENVIRONMENT: env.SENTRY_ENVIRONMENT,
      SENTRY_TRACES_SAMPLE_RATE: env.SENTRY_TRACES_SAMPLE_RATE,
      DEBUG: env.DEBUG,

      VIDEO_FETCH_DELAY_MIN_MS: env.VIDEO_FETCH_DELAY_MIN_MS,
      VIDEO_FETCH_DELAY_MAX_MS: env.VIDEO_FETCH_DELAY_MAX_MS,
    } satisfies ClientEnvironment,
  };
}

export const handle = { i18n: ['translation'] };

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>('root');
  const { i18n } = useTranslation();

  // Current theme data is stored in the cookie. The server will send it to the
  // client and we should use it when rendering the page to avoid flashing.
  const themeParseResult = themeSchema.nullable().safeParse(data?.theme);
  const theme = themeParseResult.success ? themeParseResult.data : null;

  useEffect(() => {
    if (!data?.ENV?.SENTRY_DSN) {
      console.warn('SENTRY_DSN variable is not set. Sentry disabled.');
      return;
    }

    Sentry.init({
      dsn: data.ENV.SENTRY_DSN,
      environment: data.ENV.SENTRY_ENVIRONMENT,
      tracesSampleRate: data.ENV.SENTRY_TRACES_SAMPLE_RATE,

      integrations: [
        Sentry.browserTracingIntegration({
          useEffect,
          useLocation,
          useMatches,
        }),
      ],
    });
  }, [data]);

  return (
    <html
      lang={data?.locale ?? 'en'}
      dir={i18n.dir()}
      className={cn(theme === 'dark' && 'dark')}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {data?.ENV && <PublicEnvironmentProvider {...data.ENV} />}
      </head>
      <body>
        <ThemeProviderServerHydrated initialTheme={theme}>
          <div className="flex min-h-dvh flex-col items-center">{children}</div>
        </ThemeProviderServerHydrated>
        <ScrollRestoration />
        <Scripts />
        <Toaster />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

function App() {
  const data = useLoaderData<typeof loader>();
  useChangeLanguage(data.locale);
  return (
    <EnvironmentProvider environment={data.ENV}>
      <SupabaseProvider
        supabaseUrl={data.ENV.SUPABASE_URL!}
        supabaseAnonKey={data.ENV.SUPABASE_ANON_KEY!}>
        <ApiProvider baseUrl={data.ENV.API_URL!}>
          <QueryClientProvider client={queryClient}>
            <UserProvider initialUser={data.user} initialSession={data.session}>
              <Outlet />
            </UserProvider>
          </QueryClientProvider>
        </ApiProvider>
      </SupabaseProvider>
    </EnvironmentProvider>
  );
}

export default withSentry(App);
